import { ApiError } from '@/app/lib/apiError'
import { formatMissingDays } from '@/app/lib/quant/previewView'
import { QUANT_ABORT_DETAIL } from '@/app/lib/quant/types'
import type { ViewErrorCode, ViewFailure } from '@/app/lib/quant/viewProtocol'

/**
 * Spec 004 P3 (G9): QMC's `ErrorBody.code` is a plain string, so the union is declared here: echo's
 * closed set (kept so the copied behaviour and tests stay valid; QMC never emits the `bridge_*` ones)
 * plus every code QMC's backend declares in `quant_mission_control/errors.py`. The unit test
 * `tests/app/lib/quant/quantErrorCodes.test.ts` scans that file, so a new backend code fails it until it is added here.
 */
export const ECHO_CODES = [
  'cap_full', 'run_running', 'run_not_found', 'unknown_strategy', 'strategy_unavailable', 'invalid_params',
  'bridge_unconfigured', 'bridge_down', 'docker_unavailable', 'image_missing', 'bridge_error',
  'chart_not_ready', 'chart_missing', 'chart_invalid',
  'preview_unconfigured', 'preview_down', 'preview_timeout', 'preview_failed', 'preview_unsupported',
  'instrument_not_live', 'instrument_not_offered', 'invalid_request', 'window_too_large',
  'live_not_found', 'live_cap_full', 'live_active', 'live_starting', 'live_unsupported', 'live_unavailable',
  'invalid_account', 'live_slot_busy', 'live_data_not_covered', 'live_feed_refused', 'live_start_refused',
  'live_start_failed', 'live_not_configured', 'superseded', 'data_not_covered', 'not_found',
  'invalid_config', 'invalid_config_name', 'config_limit',
] as const

/** Codes QMC emits that echo's table does not know. They carry no code-specific behaviour (`status` class). */
export const QMC_EXTRA_CODES = [
  'unauthorized', 'forbidden', 'method_not_allowed', 'conflict', 'payload_too_large', 'unsupported_media_type',
  'rate_limited', 'upstream_failed', 'internal_error', 'artifact_error', 'engine_error', 'name_conflict',
  'engine_contract_unsupported', 'data_unavailable', 'live_not_active',
] as const

export type QuantErrorCode = (typeof ECHO_CODES)[number] | (typeof QMC_EXTRA_CODES)[number]

/**
 * Codes that never reach `CODE_CLASS`: the sandbox view's `error` frames are classified by
 * `VIEW_ERROR_COPY` (keys below), and the four feed codes travel in a 200 body, never as an HTTP error.
 */
export const FEED_BODY_CODES: readonly string[] = [
  'feed_unconfigured', 'feed_unreachable', 'feed_access_denied', 'feed_contract_unsupported',
]

/**
 * Spec 038 P3: error copy and effects chosen from the server's `code` (echo-core
 * 0.63.0), with the old status rules as the fallback when `code` is absent or
 * unknown. `detail` is never parsed or pattern-matched.
 */

/** Infrastructure codes: a fixed headline, then the server's `detail` verbatim. */
const HEADLINES: Partial<Record<QuantErrorCode, string>> = {
  bridge_unconfigured: 'The Quant run-bridge is not set up on the server.',
  bridge_down: 'The Quant run-bridge is not reachable.',
  docker_unavailable: 'Docker is not available to the run-bridge.',
  image_missing: 'The echo-quant image has not been built.',
  bridge_error: 'The run-bridge refused the request.',
  preview_down: 'The preview service is not answering.',
  preview_unconfigured: 'The preview service is not set up on the server.',
  live_start_failed: 'The live session could not be started.',
  live_not_configured: 'Live sessions are not set up on the server.',
  live_unavailable: 'Live sessions are not available right now.',
}

/**
 * Every server code must be classified here (the Record is exhaustive over the Literal union):
 * `headline` = infrastructure, `domain` = `detail` is user-facing, `status` = no code-specific
 * behavior; the status rules decide (`not_found` is the preview upstream's own 404, never forwarded raw).
 */
const CODE_CLASS: Record<QuantErrorCode, 'headline' | 'domain' | 'status'> = {
  bridge_unconfigured: 'headline',
  bridge_down: 'headline',
  docker_unavailable: 'headline',
  image_missing: 'headline',
  bridge_error: 'headline',
  preview_down: 'headline',
  preview_unconfigured: 'headline',
  live_start_failed: 'headline',
  live_not_configured: 'headline',
  live_unavailable: 'headline',
  cap_full: 'domain',
  run_running: 'domain',
  unknown_strategy: 'domain',
  strategy_unavailable: 'domain',
  invalid_params: 'domain',
  chart_missing: 'domain',
  chart_invalid: 'domain',
  run_not_found: 'domain',
  chart_not_ready: 'domain',
  superseded: 'domain',
  preview_unsupported: 'domain',
  instrument_not_offered: 'domain',
  instrument_not_live: 'domain',
  invalid_request: 'domain',
  window_too_large: 'domain',
  data_not_covered: 'domain',
  preview_failed: 'domain',
  preview_timeout: 'domain',
  live_cap_full: 'domain',
  live_slot_busy: 'domain',
  live_starting: 'domain',
  live_unsupported: 'domain',
  invalid_account: 'domain',
  live_data_not_covered: 'domain',
  live_feed_refused: 'domain',
  live_start_refused: 'domain',
  live_not_found: 'domain',
  live_active: 'domain',
  not_found: 'status',
  unauthorized: 'status',
  forbidden: 'status',
  method_not_allowed: 'status',
  conflict: 'status',
  payload_too_large: 'status',
  unsupported_media_type: 'status',
  rate_limited: 'status',
  upstream_failed: 'status',
  internal_error: 'status',
  artifact_error: 'status',
  engine_error: 'status',
  name_conflict: 'status',
  engine_contract_unsupported: 'status',
  data_unavailable: 'status',
  live_not_active: 'status',
  // Spec 073 saved configs: domain codes, the server's detail is shown verbatim.
  invalid_config: 'domain',
  invalid_config_name: 'domain',
  config_limit: 'domain',
}

/** `domain` codes: `detail` is already user-facing; the code selects the effect. */
function isDomainCode(code: string): boolean {
  return Object.prototype.hasOwnProperty.call(CODE_CLASS, code) && CODE_CLASS[code as QuantErrorCode] === 'domain'
}

export function quantErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null
}

/** The code, only when this client knows it; an unknown future code counts as absent. */
function knownCode(error: unknown): QuantErrorCode | null {
  const code = quantErrorCode(error)
  if (code === null || !Object.prototype.hasOwnProperty.call(CODE_CLASS, code)) return null
  const known = code as QuantErrorCode
  return CODE_CLASS[known] === 'status' ? null : known
}

/**
 * One sentence for a failed Quant request. Status 0 errors already carry
 * "Cannot reach echo-core." or "echo-core did not answer in time.".
 */
/** True for the error raised when the caller aborted its own request. */
export function isQuantAbort(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0 && error.message === QUANT_ABORT_DETAIL
}

export function quantErrorMessage(error: unknown): string {
  if (isQuantAbort(error)) return 'The request was canceled.'
  if (!(error instanceof ApiError)) return 'Something went wrong talking to the server.'
  const code = knownCode(error)
  const headline = code === null ? undefined : HEADLINES[code]
  if (headline !== undefined) return `${headline} ${error.message}`
  if (code !== null) return error.message
  return error.status === 503 ? `Quant run-bridge unavailable. ${error.message}` : error.message
}

export function refusalKind(error: unknown): 'refused' | 'rejected' | 'other' {
  if (!(error instanceof ApiError)) return 'other'
  const code = knownCode(error)
  if (code === 'cap_full') return 'refused'
  if (code === 'unknown_strategy' || code === 'strategy_unavailable' || code === 'invalid_params') return 'rejected'
  if (code !== null) return 'other'
  if (error.status === 409) return 'refused'
  if (error.status === 422 || error.status === 400) return 'rejected'
  return 'other'
}

const LIVE_REFUSED_CODES: ReadonlySet<QuantErrorCode> = new Set<QuantErrorCode>([
  'live_active',
  'live_cap_full',
  'live_slot_busy',
  'live_starting',
  'live_data_not_covered',
  'live_feed_refused',
  'live_start_refused',
])
const LIVE_REJECTED_CODES: ReadonlySet<QuantErrorCode> = new Set<QuantErrorCode>([
  'live_unsupported',
  'invalid_params',
  'invalid_account',
  'unknown_strategy',
  'strategy_unavailable',
])

/**
 * Spec 041: what a refused live start means. `refused` = a state of the world
 * (warn line), `rejected` = the request was wrong (err line), `other` = anything
 * else. Code first; with no known code, the status decides.
 */
export function liveRefusalKind(error: unknown): 'refused' | 'rejected' | 'other' {
  if (!(error instanceof ApiError)) return 'other'
  const code = knownCode(error)
  if (code !== null) {
    if (LIVE_REFUSED_CODES.has(code)) return 'refused'
    if (LIVE_REJECTED_CODES.has(code)) return 'rejected'
    return 'other'
  }
  if (error.status === 409) return 'refused'
  if (error.status === 422 || error.status === 400) return 'rejected'
  return 'other'
}

/** Spec 041: the session no longer exists (every unknown-session call answers 404 `live_not_found`). */
export function isLiveGone(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = knownCode(error)
  return code !== null ? code === 'live_not_found' : error.status === 404
}

export function isLiveStarting(error: unknown): boolean {
  return error instanceof ApiError && knownCode(error) === 'live_starting'
}

export function isAlreadyGone(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = knownCode(error)
  return code !== null ? code === 'run_not_found' : error.status === 404
}

export function isRunStillRunning(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  const code = knownCode(error)
  return code !== null ? code === 'run_running' : error.status === 409
}

export type PreviewErrorKind =
  | 'superseded'
  | 'down'
  | 'timeout'
  | 'unconfigured'
  | 'invalid_params'
  | 'not_covered'
  | 'failed'
  | 'unavailable'
  | 'abort'
  | 'other'

const UNCONFIGURED_CODES: ReadonlySet<string> = new Set([
  'preview_unconfigured',
  'bridge_unconfigured',
  'bridge_down',
  'docker_unavailable',
  'image_missing',
  'bridge_error',
])

/**
 * Spec 039: what a failed preview request means for the panel. Chosen from the
 * server's `code`; with no code or an unknown one, from the status. `detail` is
 * never read.
 */
export function previewErrorKind(error: unknown): PreviewErrorKind {
  if (!(error instanceof ApiError)) return 'other'
  if (isQuantAbort(error)) return 'abort'
  const code = error.code
  if (code === 'superseded') return 'superseded'
  if (code === 'preview_down') return 'down'
  if (code === 'preview_timeout') return 'timeout'
  if (code !== null && UNCONFIGURED_CODES.has(code)) return 'unconfigured'
  if (code === 'invalid_params') return 'invalid_params'
  if (code === 'data_not_covered') return 'not_covered'
  if (code === 'preview_failed') return 'failed'
  if (code !== null && isDomainCode(code)) return 'other'
  if (error.status === 0) return 'timeout'
  if (error.status === 503) return 'down'
  if (error.status === 504) return 'timeout'
  if (error.status === 409) return 'superseded'
  if (error.status >= 500) return 'failed'
  return 'other'
}

// ---- Live view (WS /api/quant/view, spec 070) ------------------------------

/** Fixed copy for every relay and echo-quant 035 error code (a test asserts coverage of `viewProtocol.ts`). */
export const VIEW_ERROR_COPY: Record<ViewErrorCode | 'connection_lost', string> = {
  view_cap_reached: 'Two live views are already open.',
  view_unavailable: 'Live view is unavailable.',
  instrument_not_live: 'No live feed for this instrument.',
  data_not_covered: 'Stored data does not cover this window.',
  heartbeat_timeout: 'The live view dropped. Reconnect to resume.',
  view_failed: 'The live view failed. Reconnect to resume.',
  slow_client: 'The live view could not keep up and was closed. Reconnect to resume.',
  connection_lost: 'The live view connection closed. Reconnect to resume.',
  server_closing: 'echo-quant is restarting. Reconnect to resume.',
  unauthorized: 'Sign in again to open a live view.',
  bridge_down: 'Live view is unavailable.',
  bridge_unconfigured: 'Live view is unavailable.',
  invalid_request: 'The live view request was refused.',
  invalid_params: 'The strategy parameters were refused.',
  unknown_strategy: 'That strategy is not known.',
  strategy_unavailable: 'That strategy has no live view.',
  preview_unsupported: 'That strategy has no live view.',
  instrument_not_offered: 'That instrument is not offered for this view.',
  window_too_large: 'That window is too large for this timeframe.',
  unsupported_indicator: 'An indicator type is not supported by the live view.',
  invalid_indicator_params: 'An indicator parameter was refused.',
  indicator_limit: 'At most 8 indicators fit on one live view.',
}

/** Codes whose server `message` says which value was wrong, so it follows the fixed copy. */
const VIEW_DETAIL_CODES: ReadonlySet<string> = new Set(['invalid_params', 'invalid_indicator_params', 'window_too_large', 'invalid_request'])

export function viewFailureMessage(failure: Pick<ViewFailure, 'code' | 'message' | 'frame'>): string {
  const base = VIEW_ERROR_COPY[failure.code]
  if (failure.code === 'data_not_covered') {
    const days = failure.frame?.missing_days
    const count = failure.frame?.missing_count
    const missing = formatMissingDays(
      Array.isArray(days) ? days.filter((d): d is string => typeof d === 'string') : undefined,
      typeof count === 'number' ? count : undefined,
    )
    return missing === null ? base : `${base} ${missing}`
  }
  return VIEW_DETAIL_CODES.has(failure.code) && failure.message !== '' ? `${base} ${failure.message}` : base
}

/**
 * What the screen does about a failed view: `reconnect` = one user-initiated reopen, no automatic loop;
 * `fallback` = keep the still REST preview ("no live preview"); `none` = show the copy only.
 */
export function viewFailureEffect(code: ViewFailure['code']): 'reconnect' | 'fallback' | 'none' {
  switch (code) {
    case 'heartbeat_timeout':
    case 'view_failed':
    case 'slow_client':
    case 'connection_lost':
    case 'server_closing':
      return 'reconnect'
    case 'view_unavailable':
    case 'instrument_not_live':
    case 'view_cap_reached':
    case 'bridge_down':
    case 'bridge_unconfigured':
      return 'fallback'
    default:
      return 'none'
  }
}

/** True when `code` is one this client has classified (REST, sandbox-view frame, or feed body code). */
export function isClassifiedCode(code: string): boolean {
  return (
    Object.prototype.hasOwnProperty.call(CODE_CLASS, code) ||
    Object.prototype.hasOwnProperty.call(VIEW_ERROR_COPY, code) ||
    FEED_BODY_CODES.includes(code)
  )
}
